import { useCallback } from "react";
import { getUserDashboardPageFromPath, getUserDashboardPath } from "../utils/offerHelpers";
import { INITIAL_PORTAL_VO_FILTERS } from "../utils/portalVoHelpers";
import { trackViewContent } from "../utils/metaPixel";
import { trackFunnelEvent } from "../utils/funnelTracker";

export function createInitialDecisionAnswers() {
  return {
    operation: "",
    acquisition: "",
    hasBrand: "si",
    brand: "",
    model: "",
    condition: "",
    monthlyBudget: "",
    cashBudget: "",
    priceMin: 0,
    priceMax: 220000,
    financeAmount: "",
    entryAmount: "",
    powerMin: 70,
    powerMax: 320,
    location: "toda_espana",
    fuelFilter: "cualquiera",
    sellerType: "",
    traction: "",
    displacement: "",
    displacementMin: "",
    displacementMax: "",
    co2: "",
    co2Min: "",
    co2Max: "",
    powerKw: "",
    powerKwMin: "",
    powerKwMax: "",
    consumption: "",
    consumptionMin: "",
    consumptionMax: "",
    ageMin: 0,
    ageMax: null,
    ageFilter: "all",
    mileageMin: 0,
    mileageMax: null,
    mileageFilter: "all",
  };
}

export function createInitialSellAnswers() {
  return {
    brand: "",
    model: "",
    year: "",
    mileage: "",
    fuel: "Gasolina",
    sellerType: "particular",
  };
}

function buildNextDecisionAnswers(prev, key, value) {
  const next = { ...prev, [key]: value };

  if (key === "brand") {
    next.model = "";
  }

  if (key === "operation" || key === "acquisition") {
    next.acquisition = key === "operation" ? "" : next.acquisition;
    next.monthlyBudget = "";
    next.cashBudget = "";
    next.priceMin = 0;
    next.priceMax = 220000;
    next.financeAmount = "";
    next.entryAmount = "";
    next.powerMin = 70;
    next.powerMax = 320;
    next.location = "toda_espana";
    next.fuelFilter = "cualquiera";
    next.sellerType = "";
    next.traction = "";
    next.displacement = "";
    next.displacementMin = "";
    next.displacementMax = "";
    next.co2 = "";
    next.co2Min = "";
    next.co2Max = "";
    next.powerKw = "";
    next.powerKwMin = "";
    next.powerKwMax = "";
    next.consumption = "";
    next.consumptionMin = "";
    next.consumptionMax = "";
    next.ageMin = 0;
    next.ageMax = null;
    next.ageFilter = "all";
    next.mileageMin = 0;
    next.mileageMax = null;
    next.mileageFilter = "all";
  }

  if (key === "hasBrand" && value === "no") {
    next.brand = "";
    next.model = "";
  }

  return next;
}

export function useAdvisorController({
  clearQuestionnaireDraftFn,
  currentUser,
  entryMode,
  isUserLoggedIn,
  listingFilters,
  quickValidationAnswers,
  resetListingDiscovery,
  searchRealListing,
  setAdvancedMode,
  setAnswers,
  setApiKeyMissing,
  setDecisionAiResult,
  setDecisionAnswers,
  setDecisionError,
  setDecisionListingError,
  setDecisionListingLoading,
  setDecisionListingResult,
  setDecisionLoading,
  setEntryMode,
  vasA,
  setError,
  setListingError,
  setListingFilters,
  setLoading,
  setMultiSelected,
  setPortalVoFilters,
  setQuickValidationAnswers,
  setResult,
  setResultView,
  setSaveFeedback,
  setSelectedPortalVoOfferId,
  setSellAiResult,
  setSellAnswers,
  setSellError,
  setSellListingError,
  setSellListingLoading,
  setSellListingResult,
  setSellLoading,
  setShowAuthMenu,
  setShowUserPanel,
  setStep,
  setUserDashboardPage,
  syncBrowserPath,
  onAuthRequest,
  onLogoutUser,
  metePorDelante,
}) {
  const restartQuestionnaire = useCallback(() => {
    clearQuestionnaireDraftFn?.();
    setStep(0);
    setAnswers({});
    setMultiSelected([]);
    setResult(null);
    setError(null);
    setApiKeyMissing(false);
    setLoading(false);
    resetListingDiscovery();
  }, [clearQuestionnaireDraftFn, resetListingDiscovery, setAnswers, setApiKeyMissing, setError, setLoading, setMultiSelected, setResult, setStep]);

  const handleAuthAction = useCallback((mode) => {
    const routePage = typeof window !== "undefined"
      ? getUserDashboardPageFromPath(window.location.pathname)
      : null;

    setShowAuthMenu(false);
    setShowUserPanel(false);

    /*
     * Y se lo pide a quien sabe: abrir el dialogo de acceso.
     *
     * Aqui habia veinte lineas mas -una TERCERA copia de «ya estas dentro»,
     * con su propio mensaje- para el caso de que nadie pasara `onAuthRequest`.
     * `App` lo pasa siempre, asi que no se ejecutaban nunca: el mensaje
     * «Sesion iniciada. Ya tienes disponible tu panel personal» no existia en
     * ninguna pantalla, y no lo miraba ninguna prueba.
     */
    onAuthRequest?.(mode, { routePage });
  }, [onAuthRequest, setShowAuthMenu, setShowUserPanel]);

  const handleUserAccessClick = useCallback(() => {
    if (isUserLoggedIn) {
      const routePage = typeof window !== "undefined"
        ? getUserDashboardPageFromPath(window.location.pathname)
        : null;

      setShowAuthMenu(false);
      setShowUserPanel(false);
      setUserDashboardPage(routePage || "home");
      vasA("userDashboard");
      syncBrowserPath(getUserDashboardPath(routePage || "home"));
      return;
    }

    setShowAuthMenu((prev) => !prev);
    setShowUserPanel(false);
  }, [isUserLoggedIn, setShowAuthMenu, setShowUserPanel, vasA, setUserDashboardPage, syncBrowserPath]);

  const handleLogout = useCallback(() => {
    /* Esto apaga los dos estados de la sesion: `currentUser` e
       `isUserLoggedIn`. Aqui se apagaba solo el segundo, y el primero lo
       apagaba `resetLoggedUser`: la coherencia dependia de llamar a los dos. */
    onLogoutUser?.();
    setShowAuthMenu(false);
    setShowUserPanel(false);
    setUserDashboardPage("home");
    syncBrowserPath("/", "replace");

    if (entryMode === "userDashboard") {
      vasA(null);

      if (typeof window !== "undefined") {
        window.setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
      }
    }
  }, [entryMode, onLogoutUser, setShowAuthMenu, setShowUserPanel, vasA, setUserDashboardPage, syncBrowserPath]);

  const updateListingFilter = useCallback((key, value) => {
    if (key === "priceRange") {
      const PRICE_RANGE_MAP = {
        under_15000: { minPrice: null, maxPrice: 15000 },
        "15000_25000": { minPrice: 15000, maxPrice: 25000 },
        "25000_35000": { minPrice: 25000, maxPrice: 35000 },
        over_35000: { minPrice: 35000, maxPrice: null },
      };

      const mappedRange = PRICE_RANGE_MAP[value] || { minPrice: null, maxPrice: null };
      const nextFilters = {
        ...listingFilters,
        priceRange: value,
        minPrice: mappedRange.minPrice,
        maxPrice: mappedRange.maxPrice,
      };

      setListingFilters(nextFilters);
      setListingError(null);
      void searchRealListing(nextFilters, quickValidationAnswers);
      return;
    }

    const nextFilters = {
      ...listingFilters,
      [key]: value,
    };

    setListingFilters(nextFilters);
    setListingError(null);
    void searchRealListing(nextFilters, quickValidationAnswers);
  }, [listingFilters, quickValidationAnswers, searchRealListing, setListingError, setListingFilters]);

  const updateQuickValidationAnswer = useCallback((key, value) => {
    setQuickValidationAnswers((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, [setQuickValidationAnswers]);

  const openPortalVoOfferDetail = useCallback((offer) => {
    if (!offer?.id) {
      return;
    }

    /*
     * Se deja en la lista para que la ficha la encuentre: las del bloque de
     * concesionarios no vienen en las paginas que sirve el servidor.
     *
     * Esto estaba escrito a mano aqui, con el mismo cuerpo que en
     * `useElMercadoVo`. Era la tercera copia.
     */
    metePorDelante?.(offer);

    setSelectedPortalVoOfferId(offer.id);
    syncBrowserPath(`/marketplace-vo/${encodeURIComponent(offer.id)}`, "push");
    vasA("portalVoDetail");
    trackViewContent({
      vehicleTitle: offer.title || "",
      vehicleId: offer.id,
      price: offer.salePrice || offer.price || 0,
    });
    trackFunnelEvent({
      event_type: "offer_view",
      user_id:    currentUser?.id    || null,
      user_email: currentUser?.email || null,
      offer_id:   offer.id,
      offer_title: offer.title || "",
    });

    if (typeof window !== "undefined") {
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
    }
  }, [currentUser, metePorDelante, vasA, setSelectedPortalVoOfferId, syncBrowserPath]);

  const restart = useCallback(() => {
    clearQuestionnaireDraftFn?.();
    vasA(null);
    setUserDashboardPage("home");
    syncBrowserPath("/", "replace");
    setAnswers({});
    setMultiSelected([]);
    setResult(null);
    setResultView("analysis");
    setError(null);
    setApiKeyMissing(false);
    setLoading(false);
    setAdvancedMode(false);
    resetListingDiscovery();
    setDecisionAnswers(createInitialDecisionAnswers());
    setSellAnswers(createInitialSellAnswers());
    setPortalVoFilters({ ...INITIAL_PORTAL_VO_FILTERS });
    setSelectedPortalVoOfferId(null);
    setDecisionAiResult(null);
    setDecisionError(null);
    setDecisionLoading(false);
    setDecisionListingResult(null);
    setDecisionListingError(null);
    setDecisionListingLoading(false);
    setSellAiResult(null);
    setSellError(null);
    setSellLoading(false);
    setSellListingResult(null);
    setSellListingError(null);
    setSellListingLoading(false);
  }, [
    clearQuestionnaireDraftFn,
    resetListingDiscovery,
    vasA,
    setAdvancedMode,
    setAnswers,
    setApiKeyMissing,
    setDecisionAiResult,
    setDecisionAnswers,
    setDecisionError,
    setDecisionListingError,
    setDecisionListingLoading,
    setDecisionListingResult,
    setDecisionLoading,
    setError,
    setLoading,
    setMultiSelected,
    setPortalVoFilters,
    setResult,
    setResultView,
    setSelectedPortalVoOfferId,
    setSellAiResult,
    setSellAnswers,
    setSellError,
    setSellListingError,
    setSellListingLoading,
    setSellListingResult,
    setSellLoading,
    setUserDashboardPage,
    syncBrowserPath,
  ]);

  const updateDecisionAnswer = useCallback((key, value) => {
    setDecisionAnswers((prev) => buildNextDecisionAnswers(prev, key, value));
  }, [setDecisionAnswers]);

  const updatePortalVoFilter = useCallback((key, value) => {
    setPortalVoFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, [setPortalVoFilters]);

  return {
    handleAuthAction,
    handleLogout,
    handleUserAccessClick,
    openPortalVoOfferDetail,
    restart,
    restartQuestionnaire,
    updateDecisionAnswer,
    updateListingFilter,
    updatePortalVoFilter,
    updateQuickValidationAnswer,
  };
}
